// data/items/armor.js
// Ranged combat armor items - 6 tiers × 6 slots = 36 pieces
// Crafted from hides dropped by pet contracts

// Armor slot definitions
export const ARMOR_SLOTS = {
    HELM: { id: 'helm', name: 'Helm', equipSlot: 'head' },
    // SHOULDERS: slot id stays 'shoulders' — legacy item ids (treated_shoulders etc.)
    // and the equip slot is shared with melee Pauldrons / magic Mantle. Display name
    // reworked to 'Vambraces' to match the commissioned forearm-guard hide art.
    SHOULDERS: { id: 'shoulders', name: 'Vambraces', equipSlot: 'shoulders' },
    GLOVES: { id: 'gloves', name: 'Gauntlets', equipSlot: 'hands' },
    CHESTPLATE: { id: 'chestplate', name: 'Chestplate', equipSlot: 'body' },
    LEGGUARDS: { id: 'legguards', name: 'Legguards', equipSlot: 'legs' },
    BOOTS: { id: 'boots', name: 'Boots', equipSlot: 'feet' }
};

// Tier definitions with combat level requirements
// Ranged armor: defense + accuracy + HP (no attack bonus - damage comes from weapons/arrows)
// Trade-off: medium defense, HP sustain, good accuracy
const TIERS = {
    1: { prefix: 'Treated', color: '#8B4513', defenseBase: 3, hpBase: 0, combatLevel: 1, valueBase: 100 },
    2: { prefix: 'Green Wyrm', color: '#2E8B57', defenseBase: 9, hpBase: 5, combatLevel: 15, valueBase: 200 },
    3: { prefix: 'Blue Wyrm', color: '#4169E1', defenseBase: 15, hpBase: 15, combatLevel: 30, valueBase: 350 },
    4: { prefix: 'Red Wyrm', color: '#DC143C', defenseBase: 24, hpBase: 30, combatLevel: 45, valueBase: 550 },
    5: { prefix: 'Black Drake', color: '#1C1C1C', defenseBase: 34, hpBase: 50, combatLevel: 60, valueBase: 800 },
    6: { prefix: 'Void Drake', color: '#4B0082', defenseBase: 46, hpBase: 80, combatLevel: 75, valueBase: 1100 }
};

// Stat modifiers per slot (defense, HP, accuracy per slot)
const SLOT_MODIFIERS = {
    helm: { defense: 0.8, hp: 0.7, accuracy: 0.7, value: 0.8 },
    shoulders: { defense: 0.9, hp: 0.8, accuracy: 0.8, value: 0.9 },
    gloves: { defense: 0.6, hp: 0.5, accuracy: 0.5, value: 0.6 },
    chestplate: { defense: 1.5, hp: 1.5, accuracy: 1.5, value: 1.5 },
    legguards: { defense: 1.2, hp: 1.2, accuracy: 1.2, value: 1.2 },
    boots: { defense: 0.7, hp: 0.6, accuracy: 0.6, value: 0.7 }
};

// Generate ranged armor items
// Ranged armor: defense + HP + accuracy (no attack bonus - damage comes from weapons/arrows)
// Ranged armor HD art lives in assets/icons/equipment/rangedArmor/.
// File slot names differ from slot ids for the reworked pieces:
//   shoulders -> vambraces, gloves -> gauntlets.
// Which (tier, slot) ranged-armor pieces have HD art. Per-slot so partial sets still
// render the pieces that exist; missing slots fall back to emoji (no broken icons).
// 'all' = every slot for that tier; a Set = only those slot ids. Add as art arrives.
const RANGED_ARMOR_FILE_SLOTS = {
    helm: 'helm', shoulders: 'vambraces', gloves: 'gauntlets',
    chestplate: 'chestplate', legguards: 'legguards', boots: 'boots'
};
const RANGED_ART_SLOTS = {
    1: 'all', 2: 'all', 3: 'all', 4: 'all', 5: 'all',                  // Treated, Green/Blue/Red Wyrm, Black Drake
    6: new Set(['helm', 'chestplate', 'gloves', 'legguards', 'boots']), // Void Drake — all but vambraces
};
function rangedSlotHasArt(tier, slotId) {
    const slots = RANGED_ART_SLOTS[tier];
    return slots === 'all' || (slots instanceof Set && slots.has(slotId));
}

function generateRangedArmorItems() {
    const items = [];

    for (let tier = 1; tier <= 6; tier++) {
        const tierData = TIERS[tier];
        const prefixId = tierData.prefix.toLowerCase().replace(/ /g, '_');

        for (const [slotKey, slotData] of Object.entries(ARMOR_SLOTS)) {
            const slotId = slotData.id;
            const slotMod = SLOT_MODIFIERS[slotId];

            const defense = Math.round(tierData.defenseBase * slotMod.defense);
            const hp = Math.round(tierData.hpBase * slotMod.hp);
            const accuracy = Math.round(tierData.defenseBase * slotMod.accuracy);
            const value = Math.round(tierData.valueBase * slotMod.value);

            const item = {
                id: `${prefixId}_${slotId}`,
                name: `${tierData.prefix} ${slotData.name}`,
                color: tierData.color,
                tier: tier,
                armorType: 'ranged',  // Ranged armor crafted from hides
                slot: slotData.equipSlot,  // Equipment slot for equipping
                craftSlot: slotId,         // Original crafting slot ID
                defenseBonus: defense,     // Combat-compatible stat
                rangedAccuracy: accuracy,  // Explicit ranged accuracy
                combatLevel: tierData.combatLevel,          // Combat level requirement
                value: value,
                stats: {
                    defense: defense,
                    hp: hp
                }
            };

            // Add HD sprite for (tier, slot) pieces that have art
            if (rangedSlotHasArt(tier, slotId)) {
                item.sprite = {
                    sheet: `assets/icons/equipment/rangedArmor/${prefixId}_${RANGED_ARMOR_FILE_SLOTS[slotId]}.png`,
                    x: 0, y: 0, width: 64, height: 64
                };
            }

            items.push(item);
        }
    }

    return items;
}

// Melee armor tier definitions (Smithing) - crafted from metal bars
// Combat level requirements roughly match smithing crafting levels
// Melee armor: highest defense, small accuracy bonus, attack bonus (no multi-hit procs like ranged)
// attackBase at ~50% of original values to compensate for lack of multi-arrow procs
const MELEE_TIERS = {
    1: { prefix: 'Pyrestone', color: '#cd7f32', defenseBase: 4, attackBase: 1, combatLevel: 1, valueBase: 80 },
    2: { prefix: 'Pale Shard', color: '#c0c0c0', defenseBase: 10, attackBase: 3, combatLevel: 15, valueBase: 180 },
    3: { prefix: 'Blackiron', color: '#2d2d2d', defenseBase: 18, attackBase: 5, combatLevel: 30, valueBase: 320 },
    4: { prefix: 'Skyiron', color: '#4169e1', defenseBase: 28, attackBase: 7, combatLevel: 45, valueBase: 520 },
    5: { prefix: 'Verdant Core', color: '#228b22', defenseBase: 40, attackBase: 10, combatLevel: 60, valueBase: 780 },
    6: { prefix: 'Voidsteel', color: '#00ced1', defenseBase: 54, attackBase: 13, combatLevel: 75, valueBase: 1100 }
};

// Melee armor slot definitions (heavy armor names)
const MELEE_ARMOR_SLOTS = {
    HELM: { id: 'helm', name: 'Helm', equipSlot: 'head' },
    SHOULDERS: { id: 'shoulders', name: 'Pauldrons', equipSlot: 'shoulders' },
    GLOVES: { id: 'gloves', name: 'Gauntlets', equipSlot: 'hands' },
    CHESTPLATE: { id: 'chestplate', name: 'Chestplate', equipSlot: 'body' },
    LEGGUARDS: { id: 'legguards', name: 'Platelegs', equipSlot: 'legs' },
    BOOTS: { id: 'boots', name: 'Sabatons', equipSlot: 'feet' }
};

// Stat modifiers per slot for melee armor (defense + attack + small accuracy bonus)
const MELEE_SLOT_MODIFIERS = {
    helm: { defense: 0.8, attack: 0.8, accuracy: 0.7, value: 0.8 },
    shoulders: { defense: 0.9, attack: 0.9, accuracy: 0.8, value: 0.9 },
    gloves: { defense: 0.6, attack: 0.6, accuracy: 0.5, value: 0.6 },
    chestplate: { defense: 1.5, attack: 1.5, accuracy: 1.5, value: 1.5 },
    legguards: { defense: 1.2, attack: 1.2, accuracy: 1.2, value: 1.2 },
    boots: { defense: 0.7, attack: 0.7, accuracy: 0.6, value: 0.7 }
};

// Melee armor sprites - tier -> slot -> sprite data (HD individual files)
const MELEE_SPRITES = {
    1: { // Pyrestone - HD individual files
        helm: { sheet: 'assets/icons/equipment/meleeArmor/pyrestone_helm.png', x: 0, y: 0, width: 64, height: 64 },
        shoulders: { sheet: 'assets/icons/equipment/meleeArmor/pyrestone_pauldrons.png', x: 0, y: 0, width: 64, height: 64 },
        chestplate: { sheet: 'assets/icons/equipment/meleeArmor/pyrestone_chestplate.png', x: 0, y: 0, width: 64, height: 64 },
        legguards: { sheet: 'assets/icons/equipment/meleeArmor/pyrestone_platelegs.png', x: 0, y: 0, width: 64, height: 64 },
        boots: { sheet: 'assets/icons/equipment/meleeArmor/pyrestone_sabatons.png', x: 0, y: 0, width: 64, height: 64 },
        gloves: { sheet: 'assets/icons/equipment/meleeArmor/pyrestone_gauntlets.png', x: 0, y: 0, width: 64, height: 64 }
    },
    2: { // Pale Shard - HD individual files
        helm: { sheet: 'assets/icons/equipment/meleeArmor/paleshard_helm.png', x: 0, y: 0, width: 64, height: 64 },
        shoulders: { sheet: 'assets/icons/equipment/meleeArmor/paleshard_pauldrons.png', x: 0, y: 0, width: 64, height: 64 },
        chestplate: { sheet: 'assets/icons/equipment/meleeArmor/paleshard_chestplate.png', x: 0, y: 0, width: 64, height: 64 },
        legguards: { sheet: 'assets/icons/equipment/meleeArmor/paleshard_platelegs.png', x: 0, y: 0, width: 64, height: 64 },
        boots: { sheet: 'assets/icons/equipment/meleeArmor/paleshard_sabatons.png', x: 0, y: 0, width: 64, height: 64 },
        gloves: { sheet: 'assets/icons/equipment/meleeArmor/paleshard_gauntlets.png', x: 0, y: 0, width: 64, height: 64 }
    },
    3: { // Blackiron - HD individual files
        helm: { sheet: 'assets/icons/equipment/meleeArmor/blackiron_helm.png', x: 0, y: 0, width: 64, height: 64 },
        shoulders: { sheet: 'assets/icons/equipment/meleeArmor/blackiron_pauldrons.png', x: 0, y: 0, width: 64, height: 64 },
        chestplate: { sheet: 'assets/icons/equipment/meleeArmor/blackiron_chestplate.png', x: 0, y: 0, width: 64, height: 64 },
        legguards: { sheet: 'assets/icons/equipment/meleeArmor/blackiron_platelegs.png', x: 0, y: 0, width: 64, height: 64 },
        boots: { sheet: 'assets/icons/equipment/meleeArmor/blackiron_sabatons.png', x: 0, y: 0, width: 64, height: 64 },
        gloves: { sheet: 'assets/icons/equipment/meleeArmor/blackiron_gauntlets.png', x: 0, y: 0, width: 64, height: 64 }
    },
    4: { // Skyiron - HD individual files
        helm: { sheet: 'assets/icons/equipment/meleeArmor/skyiron_helm.png', x: 0, y: 0, width: 64, height: 64 },
        shoulders: { sheet: 'assets/icons/equipment/meleeArmor/skyiron_pauldrons.png', x: 0, y: 0, width: 64, height: 64 },
        chestplate: { sheet: 'assets/icons/equipment/meleeArmor/skyiron_chestplate.png', x: 0, y: 0, width: 64, height: 64 },
        legguards: { sheet: 'assets/icons/equipment/meleeArmor/skyiron_platelegs.png', x: 0, y: 0, width: 64, height: 64 },
        boots: { sheet: 'assets/icons/equipment/meleeArmor/skyiron_sabatons.png', x: 0, y: 0, width: 64, height: 64 },
        gloves: { sheet: 'assets/icons/equipment/meleeArmor/skyiron_gauntlets.png', x: 0, y: 0, width: 64, height: 64 }
    },
    5: { // Verdant Core - HD individual files
        helm: { sheet: 'assets/icons/equipment/meleeArmor/verdantcore_helm.png', x: 0, y: 0, width: 64, height: 64 },
        shoulders: { sheet: 'assets/icons/equipment/meleeArmor/verdantcore_pauldrons.png', x: 0, y: 0, width: 64, height: 64 },
        chestplate: { sheet: 'assets/icons/equipment/meleeArmor/verdantcore_chestplate.png', x: 0, y: 0, width: 64, height: 64 },
        legguards: { sheet: 'assets/icons/equipment/meleeArmor/verdantcore_platelegs.png', x: 0, y: 0, width: 64, height: 64 },
        boots: { sheet: 'assets/icons/equipment/meleeArmor/verdantcore_sabatons.png', x: 0, y: 0, width: 64, height: 64 },
        gloves: { sheet: 'assets/icons/equipment/meleeArmor/verdantcore_gauntlets.png', x: 0, y: 0, width: 64, height: 64 }
    },
    6: { // Voidsteel - HD individual files
        helm: { sheet: 'assets/icons/equipment/meleeArmor/voidsteel_helm.png', x: 0, y: 0, width: 64, height: 64 },
        shoulders: { sheet: 'assets/icons/equipment/meleeArmor/voidsteel_pauldrons.png', x: 0, y: 0, width: 64, height: 64 },
        chestplate: { sheet: 'assets/icons/equipment/meleeArmor/voidsteel_chestplate.png', x: 0, y: 0, width: 64, height: 64 },
        legguards: { sheet: 'assets/icons/equipment/meleeArmor/voidsteel_platelegs.png', x: 0, y: 0, width: 64, height: 64 },
        boots: { sheet: 'assets/icons/equipment/meleeArmor/voidsteel_sabatons.png', x: 0, y: 0, width: 64, height: 64 },
        gloves: { sheet: 'assets/icons/equipment/meleeArmor/voidsteel_gauntlets.png', x: 0, y: 0, width: 64, height: 64 }
    }
};

// Generate melee armor items
// Melee armor: highest defense, attack bonus (compensates for no multi-arrows), small accuracy
function generateMeleeArmorItems() {
    const items = [];

    for (let tier = 1; tier <= 6; tier++) {
        const tierData = MELEE_TIERS[tier];
        const prefixId = tierData.prefix.toLowerCase().replace(/ /g, '_');

        for (const [slotKey, slotData] of Object.entries(MELEE_ARMOR_SLOTS)) {
            const slotId = slotData.id;
            const slotMod = MELEE_SLOT_MODIFIERS[slotId];

            const defense = Math.round(tierData.defenseBase * slotMod.defense);
            const attack = Math.round(tierData.attackBase * slotMod.attack);
            const accuracy = Math.round(tierData.defenseBase * slotMod.accuracy * 0.15);  // Small accuracy from defense
            const value = Math.round(tierData.valueBase * slotMod.value);

            const item = {
                id: `${prefixId}_${slotId}`,
                name: `${tierData.prefix} ${slotData.name}`,
                color: tierData.color,
                tier: tier,
                armorType: 'melee',  // Melee armor crafted from bars
                slot: slotData.equipSlot,  // Equipment slot for equipping
                craftSlot: slotId,         // Original crafting slot ID
                defenseBonus: defense,     // Combat-compatible stat
                attackBonus: attack,       // Melee armor gives attack (compensates for no multi-arrows)
                meleeAccuracy: accuracy,   // Small melee accuracy bonus
                combatLevel: tierData.combatLevel,        // Combat level requirement
                value: value,
                stats: {
                    defense: defense,
                    attack: attack
                }
            };

            // Add sprite if available for this tier and slot
            const tierSprites = MELEE_SPRITES[tier];
            if (tierSprites && tierSprites[slotId]) {
                const spriteData = tierSprites[slotId];
                item.sprite = {
                    sheet: spriteData.sheet,
                    x: spriteData.x,
                    y: spriteData.y,
                    width: spriteData.width,
                    height: spriteData.height
                };
            }

            items.push(item);
        }
    }

    return items;
}

// Magic armor tier definitions with combat level requirements
// T3+ armor provides spellDamage bonus (multiplier added to staff's magicDamage)
const MAGIC_TIERS = {
    1: { prefix: 'Linenweave', color: '#F5F5DC', magicBase: 2, manaBase: 5, combatLevel: 1, valueBase: 120 },
    2: { prefix: 'Silkweave', color: '#FFF8DC', magicBase: 6, manaBase: 15, combatLevel: 15, valueBase: 240 },
    3: { prefix: 'Aetherweave', color: '#87CEEB', magicBase: 13, manaBase: 30, combatLevel: 30, valueBase: 420, spellDamageBase: 0.03 },
    4: { prefix: 'Runebound', color: '#9370DB', magicBase: 23, manaBase: 55, combatLevel: 45, valueBase: 660, spellDamageBase: 0.05 },
    5: { prefix: 'Shadowweave', color: '#5A5A8F', magicBase: 36, manaBase: 90, combatLevel: 60, valueBase: 960, spellDamageBase: 0.07 },
    6: { prefix: 'Voidweave', color: '#663399', magicBase: 51, manaBase: 135, combatLevel: 75, valueBase: 1320, spellDamageBase: 0.09 }
};

// Magic armor slot definitions (different names for magic gear)
const MAGIC_ARMOR_SLOTS = {
    HELM: { id: 'helm', name: 'Hood', equipSlot: 'head' },
    SHOULDERS: { id: 'shoulders', name: 'Mantle', equipSlot: 'shoulders' },
    GLOVES: { id: 'gloves', name: 'Wraps', equipSlot: 'hands' },
    CHESTPLATE: { id: 'chestplate', name: 'Robe', equipSlot: 'body' },
    LEGGUARDS: { id: 'legguards', name: 'Leggings', equipSlot: 'legs' },
    BOOTS: { id: 'boots', name: 'Slippers', equipSlot: 'feet' }
};

// Stat modifiers per slot for magic armor
const MAGIC_SLOT_MODIFIERS = {
    helm: { magic: 0.8, mana: 0.7, value: 0.8 },
    shoulders: { magic: 0.9, mana: 0.8, value: 0.9 },
    gloves: { magic: 0.6, mana: 0.5, value: 0.6 },
    chestplate: { magic: 1.5, mana: 1.5, value: 1.5 },
    legguards: { magic: 1.2, mana: 1.2, value: 1.2 },
    boots: { magic: 0.7, mana: 0.6, value: 0.7 }
};

// Magic armor HD art lives in assets/icons/equipment/magicArmor/.
// File slot names use the magic display names (helm->hood, etc.).
// All 6 tiers complete (Linenweave, Silkweave, Aetherweave, Runebound, Shadowweave, Voidweave).
const MAGIC_ARMOR_FILE_SLOTS = {
    helm: 'hood', shoulders: 'mantle', gloves: 'wraps',
    chestplate: 'robe', legguards: 'leggings', boots: 'slippers'
};
const MAGIC_SPRITE_TIERS = new Set([1, 2, 3, 4, 5, 6]);

// Generate magic armor items
function generateMagicArmorItems() {
    const items = [];

    for (let tier = 1; tier <= 6; tier++) {
        const tierData = MAGIC_TIERS[tier];
        const prefixId = tierData.prefix.toLowerCase().replace(/ /g, '_');

        for (const [slotKey, slotData] of Object.entries(MAGIC_ARMOR_SLOTS)) {
            const slotId = slotData.id;
            const slotMod = MAGIC_SLOT_MODIFIERS[slotId];

            const magic = Math.round(tierData.magicBase * slotMod.magic);
            const mana = Math.round(tierData.manaBase * slotMod.mana);
            const value = Math.round(tierData.valueBase * slotMod.value);

            const item = {
                id: `${prefixId}_${slotId}`,
                name: `${tierData.prefix} ${slotData.name}`,
                color: tierData.color,
                tier: tier,
                armorType: 'magic',  // Magic armor crafted from cloth
                slot: slotData.equipSlot,  // Equipment slot for equipping
                craftSlot: slotId,         // Original crafting slot ID
                defenseBonus: Math.round(magic * 0.5),  // Light defense
                magicAccuracy: Math.round(magic * 0.5), // Explicit magic accuracy
                combatLevel: tierData.combatLevel,      // Combat level requirement
                value: value,
                stats: {
                    magic: magic,
                    mana: mana
                }
            };

            // T3+ magic armor provides spell damage bonus
            if (tierData.spellDamageBase) {
                item.spellDamage = parseFloat((tierData.spellDamageBase * slotMod.magic).toFixed(3));
            }

            // Add HD sprite for tiers that have art
            if (MAGIC_SPRITE_TIERS.has(tier)) {
                item.sprite = {
                    sheet: `assets/icons/equipment/magicArmor/${prefixId}_${MAGIC_ARMOR_FILE_SLOTS[slotId]}.png`,
                    x: 0, y: 0, width: 64, height: 64
                };
            }

            items.push(item);
        }
    }

    return items;
}

// Export all armor types
export const RANGED_ARMOR = generateRangedArmorItems();
export const MELEE_ARMOR = generateMeleeArmorItems();
export const MAGIC_ARMOR = generateMagicArmorItems();

// Starter armor - weaker versions available from shop
// Only helm, body, legs to keep it simple - upgrade slots as you progress
export const STARTER_ARMOR = [
    // === MELEE STARTER SET (Trainee's) ===
    {
        id: 'trainee_helm',
        name: "Trainee's Helm",
        color: '#808080',
        tier: 0,
        armorType: 'melee',
        slot: 'head',
        defenseBonus: 2,      // Weaker than Pyrestone Helm (3)
        combatLevel: 1,
        shopItem: true,
        value: 15
    },
    {
        id: 'trainee_chestplate',
        name: "Trainee's Chestplate",
        color: '#808080',
        tier: 0,
        armorType: 'melee',
        slot: 'body',
        defenseBonus: 4,      // Weaker than Pyrestone Chestplate (6)
        combatLevel: 1,
        shopItem: true,
        value: 25
    },
    {
        id: 'trainee_legguards',
        name: "Trainee's Legguards",
        color: '#808080',
        tier: 0,
        armorType: 'melee',
        slot: 'legs',
        defenseBonus: 3,      // Weaker than Pyrestone Platelegs (5)
        combatLevel: 1,
        shopItem: true,
        value: 20
    },

    // === RANGED STARTER SET (Scout's) ===
    {
        id: 'scout_helm',
        name: "Scout's Cap",
        color: '#808080',
        tier: 0,
        armorType: 'ranged',
        slot: 'head',
        defenseBonus: 1,
        rangedAccuracy: 1,    // Small accuracy bonus
        combatLevel: 1,
        shopItem: true,
        value: 15
    },
    {
        id: 'scout_chestplate',
        name: "Scout's Vest",
        color: '#808080',
        tier: 0,
        armorType: 'ranged',
        slot: 'body',
        defenseBonus: 2,
        rangedAccuracy: 2,
        combatLevel: 1,
        shopItem: true,
        value: 25
    },
    {
        id: 'scout_legguards',
        name: "Scout's Trousers",
        color: '#808080',
        tier: 0,
        armorType: 'ranged',
        slot: 'legs',
        defenseBonus: 2,
        rangedAccuracy: 2,
        combatLevel: 1,
        shopItem: true,
        value: 20
    },

    // === MAGIC STARTER SET (Initiate's) ===
    {
        id: 'initiate_helm',
        name: "Initiate's Hood",
        color: '#808080',
        tier: 0,
        armorType: 'magic',
        slot: 'head',
        defenseBonus: 1,
        magicAccuracy: 1,     // Small accuracy bonus
        combatLevel: 1,
        shopItem: true,
        value: 15
        // No spellDamage - magic armor only gets this at T3+
    },
    {
        id: 'initiate_chestplate',
        name: "Initiate's Robe",
        color: '#808080',
        tier: 0,
        armorType: 'magic',
        slot: 'body',
        defenseBonus: 2,
        magicAccuracy: 2,
        combatLevel: 1,
        shopItem: true,
        value: 25
    },
    {
        id: 'initiate_legguards',
        name: "Initiate's Leggings",
        color: '#808080',
        tier: 0,
        armorType: 'magic',
        slot: 'legs',
        defenseBonus: 1,
        magicAccuracy: 2,
        combatLevel: 1,
        shopItem: true,
        value: 20
    }
];

// Combined armor array for item registry
export const ARMOR = [...RANGED_ARMOR, ...MELEE_ARMOR, ...MAGIC_ARMOR, ...STARTER_ARMOR];

// Helper to get armor by tier
export function getArmorByTier(tier) {
    return ARMOR.filter(item => item.tier === tier);
}

// Helper to get armor by slot
export function getArmorBySlot(slotId) {
    return ARMOR.filter(item => item.slot === slotId);
}

// Helper to get melee armor by tier
export function getMeleeArmorByTier(tier) {
    return MELEE_ARMOR.filter(item => item.tier === tier);
}

// Export melee tier info for UI
export { MELEE_TIERS };

export default ARMOR;
